<template>
  <v-dialog v-model="transactionsModal" persistent width="500">
    <v-card tile>
      <v-card-title class="headline">{{
          isRefund ? $t("transactionsActions.refund") : $t("transactionsCreate")
      }}</v-card-title>
      <v-card-text>
        <v-select
          style="margin-top: 20px"
          item-text="name"
          item-value="id"
          dense
          required
          :label="$t('transactionsLocation') + ' *'"
          :items="locationsConcise"
          :disabled="isRefund"
          v-model="computedLocation"
        >
        </v-select>
        <v-menu
          v-model="showDatePicker"
          :close-on-content-click="true"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="moment(selectedDate).format('DD.MM.YYYY')"
              :label="$t('transactionsDate') + ' *'"
              type="text"
              dense
              readonly
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="selectedDate"
            no-title
            scrollable
            :locale="$i18n.locale"
          ></v-date-picker>
        </v-menu>
        <v-text-field
          :label="$t('transactionsTime') + ' *'"
          :value="moment(selectedTime).format('HH:mm')"
          v-model="selectedTime"
          dense
          type="time"
          :locale="$i18n.locale"
        ></v-text-field>
        <v-text-field
          :label="$t('transactionsAmount') + ' *'"
          dense
          type="number"
          required
          v-model="transaction.amount"
          class="mt-4"
          :error-messages="transactionAmountErrors"
          @input="$v.transaction.amount.$touch()"
          @blur="$v.transaction.amount.$touch()"
        ></v-text-field>
        <v-select
            v-model="transaction.type"
            :items="transactionTypes"
            item-text="text"
            item-value="value"
            :label="$t('transactionsTypes.label') + ' *'"
            dense
            required
            style="margin-top: 20px"
            :error-messages="transactionTypeErrors"
            @input="$v.transaction.type.$touch()"
            @blur="$v.transaction.type.$touch()"
        ></v-select>
        <v-textarea
          :label="$t('paymentFormComment')"
          dense
          v-model="transaction.description"
          rows="1"
          auto-grow
          class="mt-4"
        ></v-textarea>
        <small>* {{ $t("validation") }}</small>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="red darken-1" text @click="closeForm()">{{
          $t("btnExit")
        }}</v-btn>
        <v-btn
          color="green darken-1"
          text
          @click="submitForm()"
          :disabled="$v.transaction.$invalid"
          >{{ isRefund ? $t("btnApply") : $t("btnCreate") }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { validationMixin } from "vuelidate";
import { required, maxLength } from "vuelidate/lib/validators";
import moment from "moment";

export default {
  name: "TransactionsModal",

  mixins: [validationMixin],

  props: ["transactionsModal", "transactionTypes", "selectedLocation", "isRefund", "refundItem"],

  data() {
    return {
      transaction: {
        type: null,
        amount: null,
        operation_dt: null,
        description: null,
        number: null,
        debt_open: false,
        location: this.computedLocation,
        session: null,
      },
      selectedDate: moment().format("YYYY-MM-DD"),
      selectedTime: moment().format("HH:mm"),
      showDatePicker: false,
    };
  },

  validations: {
    transaction: {
      amount: {
        required,
        maxLength: maxLength(8),
      },
      type: {
        required
      },
      location: {
        required,
      },
    },
  },

  computed: {
    ...mapGetters(["locationsConcise"]),
    computedLocation: {
      get: function () {
        return this.transaction.location;
      },
      set: function (value) {
        this.transaction.location = value;
      },
    },
    transactionAmountErrors() {
      const errors = [];
      if (!this.$v.transaction.amount.$dirty) return errors;
      !this.$v.transaction.amount.required &&
        errors.push(this.$i18n.t("validationSum"));
      !this.$v.transaction.amount.maxLength &&
        errors.push(this.$i18n.t("validationPaymentsLength"));
      return errors;
    },
    transactionTypeErrors() {
      const errors = [];
      if (!this.$v.transaction.type.$dirty) return errors;
      !this.$v.transaction.type.required &&
        errors.push(this.$i18n.t("validationSum"));
      return errors;
    },
  },

  methods: {
    ...mapActions(["setTransaction", "refundTransaction"]),
    combineDateTime() {
      return moment(
        `${this.selectedDate} ${this.selectedTime}`,
        "YYYY-MM-DD HH:mm"
      );
    },
    async submitForm() {
      const combinedDateTime = this.combineDateTime();
      this.transaction.operation_dt = moment(combinedDateTime).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");

      if (this.isRefund && this.refundItem) {
        const payload = {
          original_transaction: this.refundItem.id,
          amount: this.transaction.amount,
          location: this.refundItem.location_id,
          session: this.refundItem.session,
          operation_dt: this.transaction.operation_dt,
          description: this.transaction.description,
          type: this.transaction.type,
        };
        await this.refundTransaction({ payload, i18n: this.$i18n });
      } else {
        await this.setTransaction(this.transaction);
      }

      this.clearTransaction();
      this.$emit("reload-transactions");
      this.$emit("close-dialog");
    },
    closeForm() {
      this.clearTransaction();
      this.$emit("close-dialog");
    },
    clearTransaction() {
      this.transaction = {
        type: null,
        amount: null,
        operation_dt: null,
        description: null,
        number: null,
        debt_open: false,
        location: this.selectedLocation,
        session: null,
      };
      this.selectedDate = moment().format("YYYY-MM-DD");
      this.selectedTime = moment().format("HH:mm");
    },
  },

  watch: {
    transactionsModal(visible) {
      if (!visible) {
        this.$v.$reset();
      }
    },
    selectedLocation: {
      immediate: true,
      handler(value) {
        this.computedLocation = value;
      },
    },
    refundItem: {
      immediate: true,
      handler(item) {
        if (this.isRefund && item) {
          this.transaction.amount = item.amount * -1;
          this.transaction.type = 'refund';
        }
      }
    }
  },
};
</script>
